import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { adminApiService } from '../services/adminApiService';
import {
    LoginRequest,
    LoginRequestQueryParams,
    LoginRequestStats,
    LoginRequestResponse,
    BulkOperationResult,
} from '../types';
import { ADMIN_ERROR_MESSAGES, ADMIN_SUCCESS_MESSAGES } from '../config';

interface UseLoginRequestsReturn {
    // Data
    requests: LoginRequest[];
    loading: boolean;
    error: string | null;
    pagination: {
        current: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
    filters: LoginRequestQueryParams;
    stats: LoginRequestStats;

    // Actions
    fetchRequests: () => Promise<void>;
    handleApprove: (requestId: number, approvedBy: string) => Promise<{ success: boolean; message?: string }>;
    handleReject: (requestId: number, rejectedBy: string) => Promise<{ success: boolean; message?: string }>;
    handleBulkApprove: (requestIds: number[], approvedBy: string) => Promise<BulkOperationResult>;
    handleBulkReject: (requestIds: number[], rejectedBy: string) => Promise<BulkOperationResult>;
    handlePageChange: (page: number, pageSize?: number) => void;
    handleFilterChange: (newFilters: Partial<LoginRequestQueryParams>) => void;
    refetch: () => Promise<void>;
    calculateStats: (data: LoginRequest[]) => LoginRequestStats;
}

export const useLoginRequests = (): UseLoginRequestsReturn => {
    const [requests, setRequests] = useState<LoginRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 25,
        total: 0,
        totalPages: 0,
    });
    const [filters, setFilters] = useState<LoginRequestQueryParams>({
        status: 'all',
        page: 1,
        per_page: 25,
    });
    const [stats, setStats] = useState<LoginRequestStats>({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        todayTotal: 0,
        todayApproved: 0,
        avgResponseTime: 0,
    });

    const toast = useToast();

    const calculateStats = useCallback((data: LoginRequest[]): LoginRequestStats => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const stats = {
            total: data.length,
            pending: data.filter(r => r.status === 'pending').length,
            approved: data.filter(r => r.status === 'approved').length,
            rejected: data.filter(r => r.status === 'rejected').length,
            todayTotal: data.filter(r => new Date(r.created_at) >= today).length,
            todayApproved: data.filter(r =>
                r.status === 'approved' &&
                new Date(r.created_at) >= today
            ).length,
            avgResponseTime: calculateAverageResponseTime(data.filter(r => r.approved_at))
        };

        return stats;
    }, []);

    const calculateAverageResponseTime = (approvedRequests: LoginRequest[]): number => {
        if (approvedRequests.length === 0) return 0;

        const totalTime = approvedRequests.reduce((sum, request) => {
            if (!request.approved_at) return sum;
            const created = new Date(request.created_at);
            const approved = new Date(request.approved_at);
            return sum + (approved.getTime() - created.getTime());
        }, 0);

        return Math.round(totalTime / approvedRequests.length / (1000 * 60 * 60)); // hours
    };

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params: LoginRequestQueryParams = {
                ...filters,
                limit: pagination.pageSize,
                offset: (pagination.current - 1) * pagination.pageSize,
            };

            const result: LoginRequestResponse = await adminApiService.getLoginRequests(params);

            setRequests(result.requests);
            setPagination(prev => ({
                ...prev,
                total: result.total,
                totalPages: Math.ceil(result.total / prev.pageSize),
            }));

            // Calculate and update stats
            const calculatedStats = calculateStats(result.requests);
            setStats(calculatedStats);

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : ADMIN_ERROR_MESSAGES.FETCH_ERROR;
            setError(errorMessage);
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.current, pagination.pageSize, calculateStats, toast]);

    const handleApprove = async (requestId: number, approvedBy: string): Promise<{ success: boolean; message?: string }> => {
        try {
            const result = await adminApiService.approveLoginRequest(requestId, approvedBy);

            if (result.success) {
                toast({
                    title: 'Success',
                    description: result.message || ADMIN_SUCCESS_MESSAGES.DATA_UPDATED,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });

                // Refresh data
                await fetchRequests();

                return { success: true, message: result.message };
            }

            return { success: false, message: 'Approval failed' };
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to approve request';
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return { success: false, message: errorMessage };
        }
    };

    const handleReject = async (requestId: number, rejectedBy: string): Promise<{ success: boolean; message?: string }> => {
        try {
            const result = await adminApiService.rejectLoginRequest(requestId, rejectedBy);

            if (result.success) {
                toast({
                    title: 'Success',
                    description: result.message || ADMIN_SUCCESS_MESSAGES.DATA_UPDATED,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });

                // Refresh data
                await fetchRequests();

                return { success: true, message: result.message };
            }

            return { success: false, message: 'Rejection failed' };
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to reject request';
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return { success: false, message: errorMessage };
        }
    };

    const handleBulkApprove = async (requestIds: number[], approvedBy: string): Promise<BulkOperationResult> => {
        try {
            const result = await adminApiService.bulkApproveLoginRequests(requestIds, approvedBy);

            toast({
                title: 'Bulk Approval Completed',
                description: `Approved: ${result.successful}/${result.total} requests`,
                status: result.failed === 0 ? 'success' : 'warning',
                duration: 5000,
                isClosable: true,
            });

            // Refresh data
            await fetchRequests();

            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Bulk approval failed';
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw err;
        }
    };

    const handleBulkReject = async (requestIds: number[], rejectedBy: string): Promise<BulkOperationResult> => {
        try {
            const result = await adminApiService.bulkRejectLoginRequests(requestIds, rejectedBy);

            toast({
                title: 'Bulk Rejection Completed',
                description: `Rejected: ${result.successful}/${result.total} requests`,
                status: result.failed === 0 ? 'success' : 'warning',
                duration: 5000,
                isClosable: true,
            });

            // Refresh data
            await fetchRequests();

            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Bulk rejection failed';
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw err;
        }
    };

    const handlePageChange = (page: number, pageSize?: number) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize,
        }));
    };

    const handleFilterChange = (newFilters: Partial<LoginRequestQueryParams>) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
        setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
    };

    const refetch = fetchRequests;

    // Effect to fetch data when dependencies change
    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    return {
        // Data
        requests,
        loading,
        error,
        pagination,
        filters,
        stats,

        // Actions
        fetchRequests,
        handleApprove,
        handleReject,
        handleBulkApprove,
        handleBulkReject,
        handlePageChange,
        handleFilterChange,
        refetch,
        calculateStats,
    };
};

export default useLoginRequests;
